/**
 * PPA module — centralized data-testid registry.
 *
 * Convention: `ppa_{page}_{element}` (static) or helpers that append stable
 * config ids (`kpi_id`, `workflow_id`, `filter_id`). Add new pages as sections
 * below; keep shared utilities at the top.
 */

// =============================================================================
// Shared utilities
// =============================================================================

/** Lowercase and replace non-alphanumeric runs with underscores for stable test id segments. */
export const slugifyTestIdSegment = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

// =============================================================================
// PPA Scope (cross-page — home ribbon, modals, other routes)
// =============================================================================

export const PPA_SCOPE_TEST_ID_PREFIX = 'ppa_scope';

export const PPA_SCOPE_RIBBON = 'ppa_scope_ribbon';
export const PPA_SCOPE_EDIT_BTN = 'ppa_scope_edit_btn';
export const PPA_SCOPE_MODAL = 'ppa_scope_modal';
export const PPA_SCOPE_MODAL_CANCEL_BTN = 'ppa_scope_modal_cancel_btn';
export const PPA_SCOPE_MODAL_APPLY_BTN = 'ppa_scope_modal_apply_btn';

export const ppaScopeFilterGroupTestId = (
    filterId: string,
    prefix = PPA_SCOPE_TEST_ID_PREFIX,
): string => `${prefix}_filter_${filterId}`;

export const ppaScopeFilterOptionTestId = (
    filterId: string,
    option: string,
    prefix = PPA_SCOPE_TEST_ID_PREFIX,
): string => `${prefix}_option_${filterId}_${slugifyTestIdSegment(option)}`;

export const ppaScopeRibbonValueTestId = (
    filterId: string,
    prefix = PPA_SCOPE_TEST_ID_PREFIX,
): string => `${prefix}_value_${filterId}`;

export const ppaScopeModalTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_modal`;

export const ppaScopeModalCancelBtnTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_modal_cancel_btn`;

export const ppaScopeModalApplyBtnTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_modal_apply_btn`;

// =============================================================================
// Home page / Dashboard (`/ppa/home`)
// =============================================================================

export const PPA_HOME_KPI_TEST_ID_PREFIX = 'ppa_home';

// --- Static section ids ---
export const PPA_HOME_PAGE = 'ppa_home_page';
export const PPA_HOME_LOADING = 'ppa_home_loading';
export const PPA_HOME_HERO = 'ppa_home_hero';
export const PPA_HOME_KPI_SECTION = 'ppa_home_kpi_section';
export const PPA_HOME_KPI_SKELETON = 'ppa_home_kpi_skeleton';
export const PPA_HOME_ALERTS = 'ppa_home_alerts';
export const PPA_HOME_ACTIVITY = 'ppa_home_activity';
export const PPA_HOME_WORKFLOWS_SECTION = 'ppa_home_workflows_section';
export const PPA_HOME_WORKFLOWS_GRID = 'ppa_home_workflows_grid';
export const PPA_HOME_WORKFLOWS_LOADING = 'ppa_home_workflows_loading';
export const PPA_HOME_FOOTER_PROMPT = 'ppa_home_footer_prompt';

// --- Dynamic ids (keyed by config: kpi_id, workflow_id, tab key) ---
export const ppaKpiCardTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_card_${kpiId}`;

export const ppaKpiCardValueTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_value_${kpiId}`;

export const ppaKpiCardTrendTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_trend_${kpiId}`;

export const ppaWorkflowCardTestId = (workflowId: string): string =>
    `ppa_home_workflow_card_${workflowId}`;

export const ppaWorkflowBtnTestId = (workflowId: string): string =>
    `ppa_home_workflow_btn_${workflowId}`;

export const ppaActivityTabTestId = (tabKey: string): string =>
    `ppa_home_activity_tab_${tabKey}`;

// =============================================================================
// Performance Summary / Opportunity Assessment (`/ppa/opportunity-assessment`)
// =============================================================================
// TODO: add static + dynamic ids when instrumenting this page

// =============================================================================
// Whitespace Analysis (`/ppa/whitespace-analysis`)
// =============================================================================
// TODO: add static + dynamic ids when instrumenting this page

// =============================================================================
// Price Pack Architecture (`/ppa/pack-architecture`)
// =============================================================================
// TODO: add static + dynamic ids when instrumenting this page

// =============================================================================
// Price Simulator v3 (`/ppa/price-simulator-v3`)
// =============================================================================
// TODO: add static + dynamic ids when instrumenting this page
